import styled from 'styled-components';
import { MainHead } from '../../../components/common/head/MainHead';
import { useLayout } from '../../../hooks/screen/useLayout';
import { Accordion } from './components/Accordion';
import { CropPriceSummaryCard } from './components/CropPriceSummaryCard';
import { CropPriceSubCategoryItem } from './components/CropPriceSubCategoryItem';
import { WholesaleMarketSelectButton } from '../components/WholesaleMarketSelectButton';
import { useEffect, useState } from 'react';
import { IWholesaleMarket } from '../../../constants/cropTrends/wholesaleMarket.constant';
import { useGetCropPrices } from '../../../hooks/api/crop-trends/useGetCropPrices';
import { AnimateLoading } from '../../../components/common/loading/AnimateLoading';
import { FavoriteCropSection } from './components/FavoriteCropSection';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { pageInfo } from '../../../constants/pageInfo/pageInfo.constant';
import { createURLSearchParams } from '../../../utils/creator/createURLSearchParams.util';
import { ChooseWholesaleMarketModal } from '../components/ChooseWholesaleMarketModal';
import { useGetCropFavorite } from '../../../hooks/api/crop-trends/useGetCropFavorite.query';
import { ampli } from '../../../ampli/index';
import { useGetDownloadAppModalHeight } from '../../../hooks/modal/useGetDownloadAppModalHeight';
import { useAppDispatch, useAppSelector } from '../../../hooks/store/useStore';
import { setCropTrendsSelectedItem } from '../../../store/scrollYSlice';
import { saveAccordionStates } from '../../../store/accordionSlice';

export interface IGoToCropDetailPageParam {
  marketCode: string;
  largeCategoryCode: string;
  middleCategoryCode: string;
  middleCategoryName: string;
  smallCategoryCode?: string;
}

interface ICropCodeInfo {
  largeCategoryCode: string;
  middleCategoryCode: string;
  smallCategoryCode: string;
  smallCategoryName: string;
}

export const CropTrendsListPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();

  const isFavoriteModalOn = searchParams.get('isFavoriteModalOn');
  const cropCode = searchParams.get('cropCode');
  const cropName = searchParams.get('cropName');
  const selectedMarketsBeforeLogin = searchParams.get('selectedMarketsBeforeLogin');

  const [currentMarket, setCurrentMarket] = useState<IWholesaleMarket>({
    code: 'all',
    name: '도매시장 전체',
  });
  const [cropInfoForFavoriteModal, setCropInfoForFavoriteModal] = useState<ICropCodeInfo>({
    largeCategoryCode: '',
    middleCategoryCode: '',
    smallCategoryCode: '',
    smallCategoryName: '',
  });
  const [isCropFavoriteModalShow, setIsCropFavoriteModalShow] = useState<boolean>(false);

  const downloadAppModalHeight = useGetDownloadAppModalHeight();

  const MAIN_HEAD_HEIGHT = 48;

  useLayout({
    fixedHeader: <MainHead backButton={{ isShow: false }} headerTitle={'플루바 시세'} hasMyInfoButton />,
    fixedHeaderHeight: MAIN_HEAD_HEIGHT,
    fixedFooter: <></>,
    fixedFooterHeight: 0,
    style: { background: '#fff' },
  });

  const goToCropDetailPage = ({
    marketCode,
    largeCategoryCode,
    middleCategoryCode,
    middleCategoryName,
    smallCategoryCode,
  }: IGoToCropDetailPageParam) => {
    // 선택한 농작물 정보 저장
    const smallCategoryName = smallCategoryCode
      ? cropPrices
          ?.find((cp) => cp.middleCategoryCode === middleCategoryCode)
          ?.subItems.find((si) => si.smallCategoryCode === smallCategoryCode)?.smallCategoryName
      : undefined;

    // 아코디언 상태 저장
    dispatch(saveAccordionStates());

    dispatch(
      setCropTrendsSelectedItem({
        middleCategoryName,
        smallCategoryName,
      }),
    );

    const searchParam = createURLSearchParams({
      marketCode,
      largeCategoryCode,
      middleCategoryCode,
      smallCategoryCode,
    });
    navigate(`${pageInfo.cropTrendsDetailPage.path}?${searchParam}`);
  };

  const handleFavoriteClick = ({
    largeCategoryCode,
    middleCategoryCode,
    smallCategoryCode,
    smallCategoryName,
  }: ICropCodeInfo) => {
    setCropInfoForFavoriteModal({
      largeCategoryCode,
      middleCategoryCode,
      smallCategoryCode,
      smallCategoryName,
    });
    ampli.pvmClickCropFavoriteButton({ cultivar: smallCategoryName });
    setIsCropFavoriteModalShow(true);
  };

  const { data: cropPrices, isLoading: isCropPricesLoading } = useGetCropPrices({
    searchParam: {
      wholesaleMarketCode: currentMarket.code === 'all' ? '' : currentMarket.code,
    },
  });

  const { data: myCropFavorite } = useGetCropFavorite();

  const month = new Date(cropPrices?.[0]?.tradeCalculationDate ?? '').getMonth() + 1;
  const day = new Date(cropPrices?.[0]?.tradeCalculationDate ?? '').getDate();

  useEffect(() => {
    if (isFavoriteModalOn === 'true' && !!cropCode && !!cropName && !!selectedMarketsBeforeLogin) {
      const splitedCode = cropCode.split('-');

      if (splitedCode[0] && splitedCode[1] && splitedCode[2]) {
        setCropInfoForFavoriteModal({
          largeCategoryCode: splitedCode[0],
          middleCategoryCode: splitedCode[1],
          smallCategoryCode: splitedCode[2],
          smallCategoryName: cropName,
        });

        setIsCropFavoriteModalShow(true);
      }
    }
  }, [isFavoriteModalOn, cropCode, cropName, selectedMarketsBeforeLogin]);

  if (isCropPricesLoading) {
    return <AnimateLoading />;
  }

  if (cropPrices) {
    return (
      <>
        <Container>
          <FavoriteCropSection goToCropDetailPage={goToCropDetailPage} />
          <WholesaleMarketSection>
            <WholesaleMarketHeader top={downloadAppModalHeight + MAIN_HEAD_HEIGHT}>
              <WholesaleMarketHeaderTitleWrapper>
                <WholesaleMarketHeaderTitle>{'도매시장별 평균가'}</WholesaleMarketHeaderTitle>
                <WholesaleMarketHeaderSubTitle>{`${month}월 ${day}일의 kg당 평균가`}</WholesaleMarketHeaderSubTitle>
              </WholesaleMarketHeaderTitleWrapper>
              <WholesaleMarketSelectButton
                currentMarket={currentMarket}
                handleMarketChange={(selectedMarket) => setCurrentMarket(selectedMarket)}
                page={'list'}
              />
            </WholesaleMarketHeader>
            <WholesaleMarketBody>
              {cropPrices.map((cropPrice) => (
                <Accordion
                  key={`${cropPrice.middleCategoryName}`}
                  middleCategoryName={cropPrice.middleCategoryName}
                  header={
                    <CropPriceSummaryCard
                      label={`${cropPrice.middleCategoryName} 전체`}
                      todayAveragePrice={cropPrice.todayAveragePrice}
                      previousPrice={cropPrice.dailyChange.previousPrice}
                      changeAmount={cropPrice.dailyChange.changeAmount}
                      changeRate={cropPrice.dailyChange.changeRate}
                      lastYearWeeklyTrend={cropPrice.lastYearWeeklyTrend}
                    />
                  }
                  onHeaderClick={() =>
                    goToCropDetailPage({
                      marketCode: currentMarket.code,
                      largeCategoryCode: cropPrice.largeCategoryCode,
                      middleCategoryCode: cropPrice.middleCategoryCode,
                      middleCategoryName: cropPrice.middleCategoryName,
                      smallCategoryCode: '',
                    })
                  }
                >
                  {cropPrice.subItems.map((subItem) => (
                    <CropPriceSubCategoryItem
                      key={`${cropPrice.middleCategoryCode}-${subItem.smallCategoryCode}`}
                      label={subItem.smallCategoryName}
                      todayAveragePrice={subItem.todayAveragePrice}
                      previousPrice={subItem.dailyChange.previousPrice}
                      changeAmount={subItem.dailyChange.changeAmount}
                      changeRate={subItem.dailyChange.changeRate}
                      lastYearWeeklyTrend={subItem.lastYearWeeklyTrend}
                      largeCategoryCode={cropPrice.largeCategoryCode}
                      middleCategoryCode={cropPrice.middleCategoryCode}
                      middleCategoryName={cropPrice.middleCategoryName}
                      smallCategoryCode={subItem.smallCategoryCode}
                      myCropFavorite={myCropFavorite ?? []}
                      onFavoriteClick={() =>
                        handleFavoriteClick({
                          largeCategoryCode: cropPrice.largeCategoryCode,
                          middleCategoryCode: cropPrice.middleCategoryCode,
                          smallCategoryCode: subItem.smallCategoryCode,
                          smallCategoryName: subItem.smallCategoryName,
                        })
                      }
                      onItemClick={() => {
                        ampli.pvmClickCropCardInWholesaleMarket({
                          cultivar: subItem.smallCategoryName,
                          price_changed_history: subItem.lastYearWeeklyTrend,
                        });

                        goToCropDetailPage({
                          marketCode: currentMarket.code,
                          largeCategoryCode: cropPrice.largeCategoryCode,
                          middleCategoryCode: cropPrice.middleCategoryCode,
                          middleCategoryName: cropPrice.middleCategoryName,
                          smallCategoryCode: subItem.smallCategoryCode,
                        });
                      }}
                    />
                  ))}
                </Accordion>
              ))}
            </WholesaleMarketBody>
          </WholesaleMarketSection>
        </Container>
        <ChooseWholesaleMarketModal
          isShow={isCropFavoriteModalShow}
          closeModal={() => setIsCropFavoriteModalShow(false)}
          largeCategoryCode={cropInfoForFavoriteModal.largeCategoryCode}
          middleCategoryCode={cropInfoForFavoriteModal.middleCategoryCode}
          smallCategoryCode={cropInfoForFavoriteModal.smallCategoryCode}
          isFavoriteModalOn={isFavoriteModalOn ?? ''}
          cropCode={cropCode ?? ''}
          cropName={cropInfoForFavoriteModal.smallCategoryName ?? cropName ?? ''}
          selectedMarketsBeforeLogin={selectedMarketsBeforeLogin ?? ''}
        />
      </>
    );
  }

  return null;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;

const WholesaleMarketSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;

  padding: 16px 0px 32px 0px;
`;

const WholesaleMarketHeader = styled.div<{ top: number }>`
  display: flex;
  padding: 8px 16px 16px 16px;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  align-self: stretch;

  position: sticky;
  top: ${(props) => `${props.top}px`};
  z-index: 10;
  background-color: white;
`;

const WholesaleMarketHeaderTitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;
  flex: 1 0 0;
`;

const WholesaleMarketHeaderTitle = styled.h3`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/X-Large/xl-bold */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 23.4px */
`;

const WholesaleMarketHeaderSubTitle = styled.span`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);
  text-align: center;

  /* System/Small/s-medium */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 10.2px */
`;

const WholesaleMarketFilter = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;

  padding: 4px 16px;
  gap: 4px;

  border-radius: var(--radius-round, 80px);
  background: #f2f2f7;
`;

const WholesaleMarketFilterText = styled.div`
  color: #232429;
  text-align: center;

  /* Display/Large/l-regular */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 20.8px */

  white-space: nowrap;
`;

const WholesaleMarketBody = styled.div`
  display: flex;
  padding: 0px 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const PrimaryPriceIndicator = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

const PrimaryPriceLabel = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Medium/m-bold */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 18.2px */
`;

const PrimaryPriceValue = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
`;

const AveragePrice = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/Medium/m-bold */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 18.2px */
`;

const PriceChange = styled.span`
  color: var(--Light-Scheme-Color-primary-blue, #196eff);
  text-align: center;

  /* Display/Small/s-medium */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 15.6px */
`;

const YoYPriceComparison = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
  gap: 4px;
`;

const YoYPriceComparisonText = styled.span`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);
  text-align: center;

  /* Display/Small/s-medium */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 15.6px */
`;
